import React, { ReactNode } from 'react'

import { secondsToMilliseconds } from 'date-fns'
import { defineMessages, FormatNumberOptions, FormattedMessage, useIntl } from 'react-intl'

import { EligibilityPlanToDisplay } from '@/types'
import { priceFromCents } from '@/utils'
import {
  getCreditDurationInMonths,
  getCustomerFees,
  getCustomerLendingRate,
  getFinancedAmount,
  getInitialDeposit,
  getTotalCreditCost,
  getTotalPurchaseAmount,
  isCredit,
  isPayLater,
} from '@/utils/regulatoryFigures'
import s from 'components/Installments/LegalMentions/LegalMentions.module.css'

const messages = defineMessages({
  disclaimer: {
    id: 'legal-mentions.disclaimer',
    defaultMessage:
      "Sous réserve d'acceptation par Alma SAS (<link>almapay.com</link>), {status} (code CIB : 17408). RCS Nanterre 839100575. Contact: <mail>support@almapay.com</mail>",
  },
  financingCompany: {
    id: 'legal-mentions.disclaimer.financing-company',
    defaultMessage: "société de financement agréée par l'ACPR",
  },
  paymentInstitution: {
    id: 'legal-mentions.disclaimer.payment-institution',
    defaultMessage: "établissement de paiement agréé par l'ACPR",
  },
  creditCostBorneByMerchant: {
    id: 'legal-mentions.credit-cost-borne-by-merchant',
    defaultMessage: "Le coût du crédit est porté par l'enseigne.",
  },
})

export interface LegalMentionsProps {
  currentPlan: EligibilityPlanToDisplay
}

const LegalMentions = ({ currentPlan }: LegalMentionsProps) => {
  const { formatNumber, formatDate, formatMessage } = useIntl()

  const isFrenchTransaction = currentPlan.transaction_country?.toUpperCase() === 'FR'

  const almaDisclaimer = formatMessage(messages.disclaimer, {
    // Alma is a financing company in France and a payment institution everywhere else.
    status: formatMessage(
      isFrenchTransaction ? messages.financingCompany : messages.paymentInstitution,
    ),
    link: (chunks: ReactNode) => (
      <a
        href="https://almapay.com"
        target="_blank"
        rel="noopener noreferrer"
        className={s.disclaimerLink}
      >
        {chunks}
      </a>
    ),
    mail: (chunks: ReactNode) => (
      <a href="mailto:support@almapay.com" className={s.disclaimerLink}>
        {chunks}
      </a>
    ),
  })

  // In France, a plan that costs the customer nothing must say the merchant pays for the credit.
  const disclaimer =
    isFrenchTransaction && getTotalCreditCost(currentPlan) === 0 ? (
      <>
        {formatMessage(messages.creditCostBorneByMerchant)} {almaDisclaimer}
      </>
    ) : (
      almaDisclaimer
    )

  const formatPrice = (cents: number, options?: FormatNumberOptions) =>
    formatNumber(priceFromCents(cents), { ...(options || {}), style: 'currency', currency: 'EUR' })

  const purchaseAmount = formatPrice(currentPlan.purchase_amount)
  const financedAmount = formatPrice(getFinancedAmount(currentPlan))
  const creditDurationInMonths = getCreditDurationInMonths(currentPlan)
  const initialDeposit = formatPrice(getInitialDeposit(currentPlan))
  const customerLendingRate = formatNumber(getCustomerLendingRate(currentPlan), {
    style: 'percent',
    maximumFractionDigits: 2,
  })

  if (isCredit(currentPlan)) {
    return (
      <div className={s.legalMentions} data-testid="legal-mentions">
        <FormattedMessage
          id="legal-mentions.credit-or-pnx-without-fees"
          defaultMessage="Crédit de {financedAmount} au taux débiteur fixe de {customerLendingRate} sur {creditDurationInMonths} mois. Permettant, avec un acompte de {initialDeposit}, de financer un achat de {purchaseAmount}. {disclaimer}"
          values={{
            financedAmount,
            customerLendingRate,
            creditDurationInMonths,
            initialDeposit,
            purchaseAmount,
            disclaimer,
          }}
        />
      </div>
    )
  }

  const fees = getCustomerFees(currentPlan)
  const hasFees = fees > 0

  if (isPayLater(currentPlan)) {
    const { deferred_days: deferredDays, deferred_months: deferredMonths } = currentPlan
    const deferredDuration =
      deferredMonths > 0
        ? formatMessage(
            {
              id: 'payment-plan-strings.deferred.months',
              defaultMessage: '{months, number} {months, plural, one {mois} other {mois}}',
            },
            { months: deferredMonths },
          )
        : formatMessage(
            {
              id: 'payment-plan-strings.deferred.days',
              defaultMessage: '{days, number} {days, plural, one {jour} other {jours}}',
            },
            { days: deferredDays },
          )

    const totalDue = formatPrice(getTotalPurchaseAmount(currentPlan), {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
    const dueDate = formatDate(
      secondsToMilliseconds(currentPlan.payment_plan?.[0]?.due_date ?? 0),
      { day: 'numeric', month: 'long', year: 'numeric' },
    )

    return (
      <div className={s.legalMentions} data-testid="legal-mentions">
        {hasFees ? (
          <FormattedMessage
            id="legal-mentions.pay-later-with-fees"
            defaultMessage="Crédit de {financedAmount} au taux débiteur fixe de {customerLendingRate} sur {deferredDuration}. Permettant de financer un achat de {purchaseAmount}, incluant des frais de {fees}. Montant total dû : {totalDue}, prélevés le {dueDate}. {disclaimer}"
            values={{
              financedAmount,
              customerLendingRate,
              deferredDuration,
              purchaseAmount,
              fees: formatPrice(fees),
              totalDue,
              dueDate,
              disclaimer,
            }}
          />
        ) : (
          <FormattedMessage
            id="legal-mentions.pay-later-without-fees"
            defaultMessage="Crédit de {financedAmount} au taux débiteur fixe de {customerLendingRate} sur {deferredDuration}. Permettant de financer un achat de {purchaseAmount}. Montant total dû : {totalDue}, prélevés le {dueDate}. {disclaimer}"
            values={{
              financedAmount,
              customerLendingRate,
              deferredDuration,
              purchaseAmount,
              totalDue,
              dueDate,
              disclaimer,
            }}
          />
        )}
      </div>
    )
  }

  return (
    <div className={s.legalMentions} data-testid="legal-mentions">
      {hasFees ? (
        <FormattedMessage
          id="legal-mentions.pnx-with-fees"
          defaultMessage="Crédit de {financedAmount} au taux débiteur fixe de {customerLendingRate} sur {creditDurationInMonths} mois. Permettant, avec un acompte de {initialDeposit}, incluant des frais de {fees}, de financer un achat de {purchaseAmount}. {disclaimer}"
          values={{
            financedAmount,
            customerLendingRate,
            creditDurationInMonths,
            initialDeposit,
            fees: formatPrice(fees),
            purchaseAmount,
            disclaimer,
          }}
        />
      ) : (
        <FormattedMessage
          id="legal-mentions.credit-or-pnx-without-fees"
          defaultMessage="Crédit de {financedAmount} au taux débiteur fixe de {customerLendingRate} sur {creditDurationInMonths} mois. Permettant, avec un acompte de {initialDeposit}, de financer un achat de {purchaseAmount}. {disclaimer}"
          values={{
            financedAmount,
            customerLendingRate,
            creditDurationInMonths,
            initialDeposit,
            purchaseAmount,
            disclaimer,
          }}
        />
      )}
    </div>
  )
}

export default LegalMentions
